import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import ReplayIcon from '@mui/icons-material/Replay';
import { FONT_FAMILIES, ReelAnimationType, ReelShape, ReelTextElement, ReelTextWarp } from '../reelCanvas.types';
import { ANIMATION_OPTIONS, SHAPE_OPTIONS } from '../reelCanvasStyles';
import { ColorField } from '../controls/ColorField';
import { GlowFields, LabeledSlider, ShadowFields } from '../controls/EffectFields';
import { TextElementBody } from '../CanvasElementView';

export interface TextElementDialogProps {
    open: boolean;
    element: ReelTextElement | null;
    onClose: () => void;
    /** Live updates while the dialog is open (the canvas previews immediately). */
    onChange: (patch: Partial<ReelTextElement>) => void;
}

type TabKey = 'text' | 'style' | 'effects' | 'shape' | 'animation';

const WARP_OPTIONS: ReadonlyArray<{ value: ReelTextWarp; label: string }> = [
    { value: 'none', label: 'Straight' },
    { value: 'arch-up', label: 'Arch up' },
    { value: 'arch-down', label: 'Arch down' },
];

/** Text SideKick settings with a live preview of the element at its real aspect ratio. */
export const TextElementDialog: React.FC<TextElementDialogProps> = ({ open, element, onClose, onChange }) => {
    const theme = useTheme();
    const [tab, setTab] = useState<TabKey>('text');
    const [previewKey, setPreviewKey] = useState(0);

    if (!element) return null;
    const el = element;

    const previewScale = Math.min(1, 420 / el.width, 220 / el.height);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                <TextFieldsIcon color="secondary" />
                <Typography variant="h6" component="span" sx={{ fontWeight: 700, flex: 1 }} noWrap>
                    {el.name}
                </Typography>
                <IconButton size="small" aria-label="Close text settings" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Live preview */}
                <Box
                    sx={{
                        borderRadius: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: alpha('#000', 0.6),
                        backgroundImage: 'repeating-conic-gradient(rgba(255,255,255,0.05) 0% 25%, transparent 0% 50%)',
                        backgroundSize: '20px 20px',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 120,
                        position: 'relative',
                    }}
                >
                    <Box sx={{ width: el.width * previewScale, height: el.height * previewScale, position: 'relative' }}>
                        <Box
                            key={previewKey}
                            sx={{
                                width: el.width,
                                height: el.height,
                                transform: `scale(${previewScale})`,
                                transformOrigin: 'top left',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                            }}
                        >
                            <TextElementBody element={el} scale={previewScale} animate />
                        </Box>
                    </Box>
                    <Tooltip title="Replay animation">
                        <IconButton size="small" aria-label="Replay animation" onClick={() => setPreviewKey((k) => k + 1)} sx={{ position: 'absolute', top: 6, right: 6, color: '#fff' }}>
                            <ReplayIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}>
                    <Tab label="Text" value="text" />
                    <Tab label="Colours & border" value="style" />
                    <Tab label="Shadow & glow" value="effects" />
                    <Tab label="Shape" value="shape" />
                    <Tab label="Animation" value="animation" />
                </Tabs>

                {tab === 'text' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField
                            label="Text"
                            value={el.text}
                            onChange={(e) => onChange({ text: e.target.value })}
                            multiline
                            minRows={2}
                            maxRows={6}
                            fullWidth
                            size="small"
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 1.5 }}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Font</InputLabel>
                                <Select label="Font" value={el.fontFamily} onChange={(e) => onChange({ fontFamily: String(e.target.value) })}>
                                    {FONT_FAMILIES.map((f) => (
                                        <MenuItem key={f.label} value={f.value} sx={{ fontFamily: f.value }}>
                                            {f.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Size (px)"
                                type="number"
                                size="small"
                                value={el.fontSize}
                                onChange={(e) => onChange({ fontSize: Math.max(4, Math.min(2000, Number(e.target.value) || 0)) })}
                                inputProps={{ min: 4, max: 2000, step: 1 }}
                                InputProps={{ startAdornment: <FormatSizeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                            <ToggleButtonGroup size="small" aria-label="Text style">
                                <ToggleButton value="bold" selected={el.bold} onClick={() => onChange({ bold: !el.bold })} aria-label="Bold">
                                    <FormatBoldIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="italic" selected={el.italic} onClick={() => onChange({ italic: !el.italic })} aria-label="Italic">
                                    <FormatItalicIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="underline" selected={el.underline} onClick={() => onChange({ underline: !el.underline })} aria-label="Underline">
                                    <FormatUnderlinedIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="uppercase" selected={el.uppercase} onClick={() => onChange({ uppercase: !el.uppercase })} aria-label="Uppercase">
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                        AA
                                    </Typography>
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup size="small" exclusive value={el.align} onChange={(_e, v) => v && onChange({ align: v })} aria-label="Horizontal alignment">
                                <ToggleButton value="left" aria-label="Align left">
                                    <FormatAlignLeftIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="center" aria-label="Align centre">
                                    <FormatAlignCenterIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="right" aria-label="Align right">
                                    <FormatAlignRightIcon fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup size="small" exclusive value={el.verticalAlign} onChange={(_e, v) => v && onChange({ verticalAlign: v })} aria-label="Vertical alignment">
                                <ToggleButton value="top" aria-label="Align top">
                                    <VerticalAlignTopIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="middle" aria-label="Align middle">
                                    <VerticalAlignCenterIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="bottom" aria-label="Align bottom">
                                    <VerticalAlignBottomIcon fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, columnGap: 2.5 }}>
                            <LabeledSlider label="Character spacing (horizontal)" value={el.letterSpacing} min={-20} max={100} unit="px" onChange={(v) => onChange({ letterSpacing: v })} />
                            <LabeledSlider label="Line spacing (vertical)" value={el.lineHeight} min={0.6} max={4} step={0.05} unit="×" onChange={(v) => onChange({ lineHeight: v })} />
                            <LabeledSlider label="Padding" value={el.padding} min={0} max={200} unit="px" onChange={(v) => onChange({ padding: v })} />
                        </Box>
                    </Box>
                )}

                {tab === 'style' && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <ColorField label="Font colour" value={el.color} onChange={(c) => onChange({ color: c })} allowGradient />
                        <ColorField label="Background colour" value={el.backgroundColor} onChange={(c) => onChange({ backgroundColor: c })} allowGradient allowTransparent />
                        <ColorField label="Border colour" value={el.borderColor} onChange={(c) => onChange({ borderColor: c })} allowTransparent />
                        <LabeledSlider label="Border width" value={el.borderWidth} min={0} max={60} unit="px" onChange={(v) => onChange({ borderWidth: v })} />
                        <LabeledSlider label="Corner radius" value={el.borderRadius} min={0} max={400} unit="px" onChange={(v) => onChange({ borderRadius: v })} />
                    </Box>
                )}

                {tab === 'effects' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <ShadowFields title="Text shadow" value={el.textShadow} onChange={(v) => onChange({ textShadow: v })} />
                        <Divider />
                        <GlowFields title="Background glow" value={el.glow} onChange={(v) => onChange({ glow: v })} />
                    </Box>
                )}

                {tab === 'shape' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Container shape
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {SHAPE_OPTIONS.map((s) => (
                                    <Button
                                        key={s.value}
                                        size="small"
                                        variant={el.shape === s.value ? 'contained' : 'outlined'}
                                        color="secondary"
                                        onClick={() => onChange({ shape: s.value as ReelShape })}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        {s.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Text path (WordArt)
                            </Typography>
                            <ToggleButtonGroup size="small" exclusive value={el.warp} onChange={(_e, v) => v && onChange({ warp: v as ReelTextWarp })} aria-label="Text warp">
                                {WARP_OPTIONS.map((w) => (
                                    <ToggleButton key={w.value} value={w.value} sx={{ textTransform: 'none' }}>
                                        {w.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Arched text renders the first line along a curve; spacing and multi-line text apply to straight text.
                            </Typography>
                        </Box>
                    </Box>
                )}

                {tab === 'animation' && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Animation</InputLabel>
                            <Select label="Animation" value={el.animation.type} onChange={(e) => onChange({ animation: { ...el.animation, type: e.target.value as ReelAnimationType } })}>
                                {ANIMATION_OPTIONS.map((a) => (
                                    <MenuItem key={a.value} value={a.value}>
                                        {a.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <LabeledSlider label="Duration" value={el.animation.durationMs} min={100} max={10000} step={100} unit="ms" onChange={(v) => onChange({ animation: { ...el.animation, durationMs: v } })} />
                        <LabeledSlider label="Delay" value={el.animation.delayMs} min={0} max={10000} step={100} unit="ms" onChange={(v) => onChange({ animation: { ...el.animation, delayMs: v } })} />
                        <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                            Entrance animations play when the layer appears on the timeline (press Play). Continuous animations keep running while the layer is visible.
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="secondary" onClick={onClose}>
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TextElementDialog;
